import { compareArraysEqual } from '@op/components/FilterCommon/utils';
import { compareParams } from './data';
import {
  addTimeToDateTime,
  duration,
  formatDateFunc,
  formatSimpleTimeFunc,
  formatTimeToObjServer,
  formatTimeToServer,
  formatTimeToShow,
  getFilterDefaultDay,
  getFilterTodayTime,
  getIsToday,
  getQOQText,
  getTimeZoneUtc,
  getTodayTime,
  getUserUtcTime,
  getUtcFilterDefaultDay,
  getUtcFilterDefaultWeek,
  getUtcTodayTime,
  momentTimeAAfterB,
  roundDownMinutes,
  subtractTimes,
} from './time';
import {
  isValidUrl,
  openWindowUrl,
  urlToMap,
  urlToObj,
  clickTopicToFeedsWithTab,
  objToUrl,
  stripBasePath,
} from './url';
import {
  formatThousandthValue,
  percentageFormat,
  percentageFormatRpo,
  percentYAxisFormat,
  reviewsFormat,
  specialSecimalFormatPercentage,
  specialSecimalFormatPercentagePro,
  yAxleFormat,
  dealwithAbsValue,
  scoreFormat,
} from './value';
import {
  initialUpperCase,
  sortArrayAuxiliaryFunc,
  sortMapAuxiliaryFunc,
  getPercentageInfo,
  getPercentageValue,
  sortTimeListFunc,
  getRationPercentValue,
  getMinuteTimeSpaceArray,
  scrollToAnchorPoint,
  transLanguage,
} from '.';
import {
  copyMarkdownText,
  markdownToText,
  markdownToHtmlFile,
  mdContentToHtml,
  replaceGptText,
  copyHtmlToClipboard,
  copyTextToClipBoard,
  preprocessHtml,
} from './text';
import { downloadHtmlToFile, downloadZip } from './file';
import { downloadHtmlToImage, judgeImage } from './image';
import { isEmpty } from '@gp/utils/common';
import {
  dealwithKeyListValue,
  dealwithMoreKeysList,
  dealwithQOQ,
  dealwithRatio,
  dealwithSingleTableData,
  dealwithTableData,
} from './requestUtil';
import {
  formatTimeXAxisText,
  getChartPosition,
  getChartsGradientColor,
  getTooltipEnterablePosition,
  specialRound,
  getRealXAxisValue,
  getTooltipEnterablePositionFunc,
} from './chart';
import {
  findMenuItem,
  gameIsNormalLevel,
  getGameInfo,
  getIsHighGame,
} from './game';
import { getStatsDetailParams } from './storageUtils';
import { getValidation } from './feeds';
import {
  getContentDetail,
  getFeedsContentText,
  getFeedsLiveContentText,
  getFeedsTitleText,
  getFeedsTopicText,
  getNewsContentText,
} from './languageValue';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { downloadAction } from '@op/pages/download/utils';
import { listTimeTitleFunc } from '@op/components/ChartCommon/formatter';
import { getAxisPointer } from '@op/components/EChartCommon';

/**
 * 根据 `isPercent` 参数选择性地格式化整数轴或百分比轴数据。
 *   - 如果 `isPercent` 为 `true`，则使用 `percentYAxisFormat` 进行格式化。
 *   - 否则，使用 `yAxleFormat` 进行格式化。
 * @param value 要格式化的数值
 * @param isPercent
 * @param isMin 0% 0% 1% 1% 这种轴出现的时候，将这个值设置为true，保留两位小数，保证能正常显示出轴
 * @returns 返回格式化后的字符串
 */
function reviewsPercentFormat(value: number, isPercent = false, isMin = false) {
  if (isPercent) return percentYAxisFormat(value, isMin);
  return yAxleFormat(value);
}

/**
 * ECharts 图表配置方法集合
 *
 * @property {Function} reviews - 格式化整数轴数据。使用 `yAxleFormat` 函数进行格式化。
 * @property {Function} percent - 格式化百分比轴数据。使用 `percentYAxisFormat` 函数进行格式化。
 * @property {Function} reviewsPercent - 根据 `isPercent` 参数选择性地格式化整数轴或百分比轴数据。
 *   - 如果 `isPercent` 为 `true`，则使用 `percentYAxisFormat` 进行格式化。
 *   - 否则，使用 `yAxleFormat` 进行格式化。
 * @property {Function} gradientColor - 获取echarts渐变区域色
 * @property {Function} ceil - 特殊四舍五入
 * @property {Function} position - 获取图表位置（已废弃，请使用 `enterablePosition` 替代）
 * @property {Function} enterablePosition - tooltip enterable 模式下的智能定位函数
 *   - 垂直方向：默认显示在鼠标正上方（默认距离 10px），支持自定义固定位置（通过 config 参数配置 fixedY、topOffset、bottomOffset 或 verticalOffset）
 *   - 水平方向：优先居中，靠右时自动左移避免被菜单遮挡
 * @property {Function} timeX - 格式化时间X轴文本
 * @property {Function} xAxisValue - 获取真实的 X 轴值（处理不同日期类型的格式转换）
 */
export const DBO_EC = {
  // Y 轴格式化
  reviews: yAxleFormat,
  percent: percentYAxisFormat,
  reviewsPercent: reviewsPercentFormat,
  // ECharts 图表相关
  gradientColor: getChartsGradientColor,
  ceil: specialRound,
  // @deprecated 请使用 `enterablePosition` 替代
  position: getChartPosition,
  enterablePosition: getTooltipEnterablePosition,
  enterablePositionFunc: getTooltipEnterablePositionFunc,
  // X轴格式化
  timeX: formatTimeXAxisText,
  xAxisValue: getRealXAxisValue,
  // tool 时间使用这个格式化
  toolTime: listTimeTitleFunc,
  axisPointer: getAxisPointer,
};

/**
 * y轴格式化方法集合
 *
 * @deprecated 已废弃，请使用 `DBO_EC` 替代
 */
export const DBO_Y = {
  reviews: DBO_EC.reviews,
  percent: DBO_EC.percent,
  reviewsPercent: DBO_EC.reviewsPercent,
};

/**
 * 格式化数据方法集合
 *
 * @property {Function} reviews - 格式化整数数据。使用 `reviewsFormat` 函数进行格式化。
 * @property {Function} ratio - 百分比格式化，支持正负数
 * @property {Function} ratio_min - 小于0.01% 格式化为 < 0.01%
 * @property {Function} ratio_min_pro - 小于0.01% 的默认保留4位小数，如果小于0.0001% 格式化为 < 0.01%
 * @property {Function} percentage - 计算占比，返回带百分号的字符串
 * @property {Function} percentageValue - 计算占比，返回带数值
 * @property {Function} formatThousand - 千分位格式化，1000 转换为 1,000
 */
export const DBO_V = {
  reviews: reviewsFormat,
  ratio: percentageFormatRpo,
  ratio_min: specialSecimalFormatPercentage,
  ratio_min_pro: specialSecimalFormatPercentagePro,
  is_empty: isEmpty,
  percentage: getPercentageInfo,
  percentageValue: getPercentageValue,
  formatThousand: formatThousandthValue,
  qoq: getRationPercentValue,
  percentageFormat,
  abs: dealwithAbsValue,
  sentiment: (value: number, defaultValue?: string | number | undefined) =>
    scoreFormat(value, 2, defaultValue),
  score: scoreFormat,
};

/**
 * 文本文件处理
 *
 *
 */
export const DBO_T = {
  copy: copyHtmlToClipboard,
  copyText: copyTextToClipBoard,
  upperFirst: initialUpperCase,
  copyMDToText: copyMarkdownText,
  mdToText: markdownToText,
  mdToHtml: mdContentToHtml,
  mdToHtmlFile: markdownToHtmlFile,
  htmlToPdf: downloadHtmlToFile,
  replaceMd: replaceGptText,
  zip: downloadZip,
  editorPreprocessHtml: preprocessHtml,
};

/**
 * 链接处理
 * @property {Function} isUrl - 判断是否为链接地址
 * @property {Function} open - 打开外部链接
 * @property {Function} detailPage - 获取详情页参数
 * @property {Function} query - URL 转 Map 对象
 * @property {Function} queryObj - URL 转普通对象
 * @property {Function} toFeeds - 跳转到 Feeds 页面（自动判断 tab）
 *
 */
export const DBO_U = {
  isUrl: isValidUrl,
  open: openWindowUrl,
  detailPage: getStatsDetailParams,
  query: urlToMap,
  queryObj: urlToObj,
  toFeeds: clickTopicToFeedsWithTab,
  go: goStatsDetailAction,
  download: downloadAction,
  objToUrl,
  stripBasePath,
};

/**
 * 时间方法
 * @property {Function} filterRangeDay - 获取过滤器时间格式默认值
 * @property {Function} timeTitle - 以filter的startTime和endTime生成一个title
 * @property {Function} isToday - 判断filter是否选为今天
 */
export const DBO_TM = {
  filterRangeDay: getFilterDefaultDay,
  utcFilterRangeDay: getUtcFilterDefaultDay,
  utcFilterRangeWeek: getUtcFilterDefaultWeek,
  timeTitle: formatTimeToShow,
  isToday: getIsToday,
  sFormat: formatSimpleTimeFunc,
  duration,
  utc: getUtcTodayTime,
  userUtc: getUserUtcTime,
  today: getTodayTime,
  filterToday: getFilterTodayTime,
  timezone: getTimeZoneUtc,
  isAfter: momentTimeAAfterB,
  formatFunc: formatDateFunc,
  minuteTimeSpace: getMinuteTimeSpaceArray,
  serverTime: formatTimeToServer,
  serverTimeObj: formatTimeToObjServer,
  floor: roundDownMinutes,
  add: addTimeToDateTime,
  subtract: subtractTimes,
  qoqTxt: getQOQText,
};

/**
 * 数据方法
 * @property {Function} compareParams - 对比两个数据
 *
 */
export const DBO_D = {
  compareDeep: compareParams,
  compareArray: compareArraysEqual,
};

/**
 * 数组集合方法
 * @property {Function} sortByMap - 数组排序的方法，通过提供的对象进行排序。
 * @property {Function} sortByArr - 数组排序的方法，通过提供的数组排序。
 */
export const DBO_ARR = {
  sortByMap: sortMapAuxiliaryFunc,
  sortByArr: sortArrayAuxiliaryFunc,
  sortByTime: sortTimeListFunc,
};

/**
 * 处理请求数据的方法
 * @property {Function} moreKeys - 针对一个对象里多个key的数据处理。
 */
export const DBO_REQ = {
  moreKeys: dealwithMoreKeysList,
  listKeys: dealwithKeyListValue,
  qoq: dealwithQOQ,
  ratio: dealwithRatio,
  table: dealwithTableData,
  singleTable: dealwithSingleTableData,
};

/**
 * 图片处理方法
 * @property {Function} screenshot - Html 截图
 * @property {Function} judge - 判断图片是否可以加载
 */
export const DBO_IMG = {
  screenshot: downloadHtmlToImage,
  judge: judgeImage,
};

/**
 * 所有游戏相关方法
 * @property {Function} findMenu - 通过菜单名称查找整个菜单信息
 */
export const DBO_GAME = {
  findMenu: findMenuItem,
  params: getGameInfo,
  isNormal: gameIsNormalLevel,
  isHigh: getIsHighGame,
};

export const DBO_FEEDS = {
  valid: getValidation,
  content: getFeedsContentText,
  newsContent: getNewsContentText,
  newsTitle: getFeedsTitleText,
  topic: getFeedsTopicText,
  liveTitle: getFeedsLiveContentText,
  origin: getContentDetail,
};

export const DBO_PAGE = {
  scroll: scrollToAnchorPoint,
};

export const DBO_TRANS = {
  language: transLanguage,
};
